import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { ConfusionPairRow } from "../components/ConfusionPairsTable";

export type ConfusionPairsTier = "hot" | "cold";
export type ConfusionPairsBucket = "day" | "week" | "month";

export interface ConfusionPairsResponse {
  tier: ConfusionPairsTier;
  bucket: ConfusionPairsBucket;
  rows: ConfusionPairRow[];
}

export function useConfusionPairs(
  jobTypeId?: string,
  from?: string,
  to?: string,
  bucket?: ConfusionPairsBucket,
) {
  const [data, setData] = useState<ConfusionPairsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (jobTypeId) params.set("jobTypeId", jobTypeId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<ConfusionPairsResponse>(`/analytics/confusion-pairs${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load confusion pairs");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
